import React, { useState } from 'react';
import { IconButton } from '@material-ui/core';
import { Create as EditIcon, Visibility as ViewIcon } from '@material-ui/icons';
import Table, {
  columnType,
  tempFilteredRows,
} from '../../../components/ClearingTable/Table';
import Button from '../../../components/Buttons/Button';
import AutoCompleteAccountNo from '../../../components/AutoComplete/AutoCompleteAccountNo';
import AutoCompleteAccountName from '../../../components/AutoComplete/AutoCompleteAccountName';
import SelectSystemCode from '../../../components/Dropdown/SelectSystemCode';
import Text from '../../../components/Typography/Text';
import TextField from '../../../components/Textfields/TextField';
import {
  notifyInfo,
  notifyError,
  notifySuccess,
} from '../../../components/Messages/Notification';
import { listEntitlement, updateEntitlement } from '../../../services/EntitlementService';
import QueryParam from '../../../services/QueryParamService';
import SelectEntryType from '../../../components/AutoComplete/SelectEntryType';
import SelectCusip from '../../../components/AutoComplete/SelectCusip';
import EditEntitlementModal from './Components/EditEntitlementModal';
import ViewEntitlementModal from './Components/ViewEntitlementModal';
import AutoCompleteSymbol from '../../../components/AutoComplete/AutoCompleteSymbol';
import { getSystemDate } from '../../../services/ProfileService';
import {
  CustomBodyRenderCurrencyLeft,
  CustomBodyRenderDate,
  CustomBodyRenderAlign,
  CustomBodyRenderHtmlString,
  CustomBodyRenderQty,
} from '../../../components/Table/CustomBodyRender';
import { formatPbDate } from '../../../lib/fmt';
import { formatQty, formatCurrency } from '../../../lib/fmt2';
import { useConfirm } from 'material-ui-confirm';

export default function Entitlement() {
  const [lockTable, setLockTable] = React.useState(true);
  const [rows, setRows] = React.useState([]);
  const [rowData, setRowData] = React.useState({});
  const [open, setOpen] = React.useState({
    edit: false,
    view: false,
  });
  const [loading, setLoading] = React.useState({
    search: false,
    save: false,
  });
  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      textNumber: '',
      entryType: '',
      accountNo: '',
      accountName: '',
      accountType: '',
      cusip: '',
      symbol: '',
      fromSystemDate: '',
      toSystemDate: '',
      status: 'Pending',
      dateType: 'Process Date',
      type: '',
    })
  );
  const [footerData, setFooterData] = useState({});
  const [pagination, setPagination] = useState({});
  const [searchDataCsv, setSearchDataCsv] = React.useState([]);
  const [previousRows, setPreviousRows] = useState(0);
  const confirm = useConfirm();

  const columns = [
    {
      name: 'actions',
      label: 'Actions',
      type: columnType.buttons,
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <div className="grd-row">
              <div className="grd-cell nm">
                <IconButton
                  aria-label="edit"
                  onClick={() => handleOpen(tempFilteredRows[dataIndex])}
                >
                  <EditIcon style={{ height: 24, width: 24 }} />
                </IconButton>
              </div>
              <div className="grd-cell nm">
                <IconButton
                  aria-label="view"
                  onClick={() => handleView(tempFilteredRows[dataIndex])}
                >
                  <ViewIcon style={{ height: 24, width: 24 }} />
                </IconButton>
              </div>
            </div>
          );
        },
        sort: false,
      },
    },
    {
      name: 'entitlementId',
      label: 'Entitlement ID',
      options: {
        display: false,
      },
    },
    {
      // Already converted to string in handlesearch
      name: 'processDate',
      label: 'Process Date',
    },
    {
      // Already converted to string in handlesearch
      name: 'payableDate',
      label: 'Payable Date',
    },
    {
      name: 'declarationDate',
      label: 'Declaration Date',
      options: {
        customBodyRender: CustomBodyRenderDate,
      },
    },
    {
      // Already converted to string in handlesearch
      name: 'recordDate',
      label: 'Record Date',
    },
    {
      name: 'expirationDate',
      label: 'Expiration Date',
      options: {
        customBodyRender: CustomBodyRenderDate,
      },
    },
    {
      // Already converted to string in handlesearch
      name: 'positionDate',
      label: 'Position Date',
    },

    {
      // Already converted to string in handlesearch
      name: 'systemDate',
      label: 'System Date',
    },

    {
      name: 'textNumber',
      label: 'Text Number',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'symbol',
      label: 'Symbol',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'entryType',
      label: 'Entry Type',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountNo',
      label: 'Account No',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'accountName',
      label: 'Account Name',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'originalCusip',
      label: 'Original Cusip',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'cusip',
      label: 'Cusip',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        display: false,
      },
    },
    {
      name: 'type',
      label: 'Type',
      options: {
        customBodyRender: CustomBodyRenderAlign,
      },
    },
    {
      name: 'qtyRate',
      label: 'Qty Rate',
      options: {
        customBodyRender: CustomBodyRenderQty,
        visible: false,
      },
      addFooter: true,
      type: columnType.price,
    },
    {
      name: 'cashRate',
      label: 'Cash Rate',
      options: {
        customBodyRender: CustomBodyRenderQty,
        visible: false,
      },
      addFooter: true,
      type: columnType.price,
    },
    {
      name: 'dtccCashRate',
      label: 'DTCC Cash Rate',
      options: {
        customBodyRender: CustomBodyRenderQty,
        visible: false,
      },
      addFooter: true,
      type: columnType.price,
    },
    {
      name: 'cash',
      label: 'Cash',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: 'amount',
    },
    {
      name: 'qty',
      label: 'Qty',
      options: {
        customBodyRender: CustomBodyRenderQty,
      },
      addFooter: true,
      type: 'qty',
    },
    {
      name: 'fractionalCash',
      label: 'Fractional Cash',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
        visible: false,
      },
      addFooter: true,
      type: 'amount',
    },
    {
      name: 'fractionalQty',
      label: 'Fractional Qty',
      options: {
        customBodyRender: CustomBodyRenderQty,
        visible: false,
      },
      addFooter: true,
      type: columnType.quantity,
    },
    {
      name: 'taxRate',
      label: 'Tax Rate',
      options: {
        customBodyRender: CustomBodyRenderQty,
        visible: false,
      },
      addFooter: true,
      type: columnType.price,
    },
    {
      name: 'taxCountry',
      label: 'Tax Country',
      options: {
        visible: false,
      },
    },
    {
      name: 'noCil',
      label: 'No CIL',
      options: {
        visible: false,
      },
    },
    {
      name: 'description',
      label: 'Description',
      options: {
        customBodyRender: CustomBodyRenderHtmlString,
        visible: false,
      },
    },
    {
      name: 'voluntary',
      label: 'Voluntary',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        visible: false,
      },
    },
    {
      name: 'voluntaryAction',
      label: 'Voluntary Action',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        visible: false,
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        customBodyRender: CustomBodyRenderAlign,
        visible: false,
      },
    },
    {
      name: 'detail',
      label: 'Detail',
      options: {
        customBodyRender: CustomBodyRenderHtmlString,
        visible: false,
      },
    },
    {
      name: 'trnsId',
      label: 'TRNS ID',
      options: {
        visible: false,
      },
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    selectableRows: 'none',
    responsive: 'standard',
    serverSide: true,
  };

  const init = async () => {
    const systemDate = await getSystemDate();

    setSearchData({
      ...searchData,
      fromSystemDate: searchData.fromSystemDate ? searchData.fromSystemDate : systemDate,
      toSystemDate: searchData.toSystemDate ? searchData.toSystemDate : systemDate,
    });
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line
  }, []);

  const handleSearchDataChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({ ...searchData, [input.name]: input.value });
  };

  const getCsvData = async () => {
    if (lockTable === true) {
      return [];
    }
    const MAX_RECORDS_ALLOWED = 100000;

    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: MAX_RECORDS_ALLOWED,
    };

    const data = await listEntitlement(searchDataCsv, paginationCopy);

    const csvData = data.entitlementsList.map((data) => {
      data.systemDate = formatPbDate(data.systemDate);
      data.processDate = formatPbDate(data.processDate);
      data.positionDate = formatPbDate(data.positionDate);
      data.recordDate = formatPbDate(data.recordDate);
      data.payableDate = formatPbDate(data.payableDate);

      return data;
    });

    if (data.entitlementsList.length >= MAX_RECORDS_ALLOWED) {
      confirm({
        description:
          'Only ' +
          MAX_RECORDS_ALLOWED.toLocaleString() +
          ' items will be downloaded, if you need more please use the metabase.',
        confirmationText: 'I understand',
      });
    }

    return csvData;
  };

  const handleSearch = async (paginationChange) => {
    try {
      setLoading({ ...loading, search: true });
      if (paginationChange !== true) {
        setLockTable(false);
      }
      if (paginationChange === true) {
        if (lockTable === true) {
          return;
        }
      }

      QueryParam.set(searchData);

      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };

      if (previousRows !== 0) {
        if (previousRows < pagination.rowsPerPage * pagination.pageNo) {
          paginationCopy.pageNo = 0;
        }
      }
      if (paginationChange !== true) {
        paginationCopy.filterNames = [];
        paginationCopy.filterValues = [];
      }

      const data = await listEntitlement(searchData, paginationCopy);

      let rowsCopy = data.entitlementsList;

      const rowsCopyOutput = rowsCopy.map((data) => {
        let tempDate = formatPbDate(data.payableDate);
        data.payableDate = tempDate;

        let tempDate2 = formatPbDate(data.positionDate);
        data.positionDate = tempDate2;

        let tempDate3 = formatPbDate(data.processDate);
        data.processDate = tempDate3;

        let tempDate4 = formatPbDate(data.recordDate);
        data.recordDate = tempDate4;

        let tempDate5 = formatPbDate(data.systemDate);
        data.systemDate = tempDate5;

        return data;
      });

      setSearchDataCsv(searchData);
      setPreviousRows(data.summary.totalRows);

      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);
      setRows(rowsCopyOutput);

      if (paginationChange !== true) {
        notifyInfo(
          data.summary.totalRows > 0
            ? data.summary.totalRows + ' search result(s)'
            : 'No records found.'
        );
      }

      setFooterData({
        cash: formatCurrency(data.summary.cash),
        qty: formatQty(data.summary.qty),
        qtyRate: formatQty(data.summary.qtyRate),
        cashRate: formatQty(data.summary.cashRate),
        taxRate: formatQty(data.summary.taxRate),
        dtccCashRate: formatQty(data.summary.dtccCashRate),
        fractionalCash: formatCurrency(data.summary.fractionalCash),
        fractionalQty: formatQty(data.summary.fractionalQty),
      });
    } catch (error) {
      notifyError(error.message);
      console.log(error);
      return;
    } finally {
      setLoading({ ...loading, search: false });
    }
  };

  const handleView = async (data) => {
    setRowData(data);
    setOpen({
      ...open,
      view: true,
    });
  };

  const handleOpen = async (data) => {
    setRowData(data);
    setOpen({
      ...open,
      edit: true,
    });
  };

  const handleClose = async (data) => {
    if (!data) {
      setOpen({
        ...open,
        edit: false,
        view: false,
      });
      return;
    }

    try {
      const resp = await updateEntitlement(data);
      handleSearch(false);
      notifySuccess(' Entitlement has been updated.');
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    }

    setOpen({
      ...open,
      edit: false,
    });
  };

  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Entitlement" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading.search}
              loading={loading.search}
              type="search"
              label={loading.search ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <SelectSystemCode
              name="dateType"
              label="Date Type"
              placeholder="Blank"
              type="Date Type Announcement"
              value={searchData.dateType}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <TextField
              required
              name="fromSystemDate"
              label="From System Date"
              type="date"
              value={searchData.fromSystemDate}
              onChange={handleSearchDataChange}
              inputProps={{
                max: searchData.toSystemDate,
              }}
            />
          </div>
          <div className="grd-cell">
            <TextField
              required
              name="toSystemDate"
              label="To System Date"
              type="date"
              value={searchData.toSystemDate}
              onChange={handleSearchDataChange}
              inputProps={{
                min: searchData.fromSystemDate,
              }}
            />
          </div>
          <div className="grd-cell">
            <SelectEntryType
              freeSolo={true}
              name="entryType"
              label="Entry Type"
              note="ReOrg"
              value={searchData.entryType}
              onChange={handleSearchDataChange}
            ></SelectEntryType>
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <AutoCompleteAccountNo
              freeSolo={true}
              name="accountNo"
              type="client"
              value={searchData.accountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountName
              freeSolo={true}
              name="accountName"
              value={searchData.accountName}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              name="type"
              label="Type"
              type="Sub Account Type"
              subType="-"
              value={searchData.type}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteSymbol
              name="symbol"
              label="Symbol"
              value={searchData.symbol}
              onChange={handleSearchDataChange}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <SelectCusip
              name="cusip"
              label="Cusip"
              value={searchData.cusip}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <TextField
              name="textNumber"
              label="Text Number"
              value={searchData.textNumber}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              name="status"
              label="Status"
              placeholder="Blank"
              type="Status"
              subType="Journal"
              value={searchData.status}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell"> </div>
        </div>
      </div>
      <div className="mt-20">
        <Table
          title={'Entitlement'}
          data={rows}
          columns={columns}
          options={options}
          footerData={footerData}
          onSearch={handleSearch}
          pagination={pagination}
          setPagination={setPagination}
          getCsvData={getCsvData}
        />
      </div>
      {open.edit && (
        <EditEntitlementModal
          data={rowData}
          open={open.edit}
          close={handleClose}
          loading={loading.save}
        ></EditEntitlementModal>
      )}
      {open.view && (
        <ViewEntitlementModal
          data={rowData}
          open={open.view}
          close={handleClose}
          loading={loading.save}
        ></ViewEntitlementModal>
      )}
    </form>
  );
}
